import type {
  IContentDiscoveryService,
  SSEConnectionState,
  SSEEvent,
  SSEEventCallback,
  SSEStateCallback,
} from './types';
import { recordSyncSample } from '~/services/time/serverClock';

/**
 * Descoberta de mudança de conteúdo por consulta periódica, no lugar de uma
 * conexão SSE aberta.
 *
 * Implementa a mesma `IContentSSEService`, então a troca é de configuração: o
 * resto do player não sabe qual dos dois está em uso.
 *
 * ## Por que existe
 *
 * Medido em produção em 2026-08-08: o edge da Railway aceita 10.000 conexões
 * concorrentes por serviço, e o broadcast SSE entrega ~8-9.000 envios por
 * segundo. O alvo é 20.000 telas, com 15.000 como operação normal — o dobro do
 * que qualquer mecanismo de conexão persistente comporta.
 *
 * Na mesma escala de 10.000 telas, medido no mesmo dia:
 *
 * | | SSE | consulta |
 * |---|---|---|
 * | cobertura | 87,78% | 100% |
 * | p95 | 12.240 ms | 660 ms |
 *
 * ## Como escapa do teto de conexões
 *
 * Sem conexão persistente, o número de conexões concorrentes deixa de ser
 * *N telas* e passa a ser *taxa × duração da requisição*. Com 20.000 telas
 * consultando a cada 5 s: 4.000 req/s e ~1.200 conexões simultâneas — 36% do
 * teto de requisições e 12% do de conexões.
 *
 * ## Os dois atrasos, e por que são diferentes
 *
 * O servidor devolve dois números, e confundi-los custa caro:
 *
 * - `poll_after_seconds` — quando voltar a **perguntar**. Barato: a resposta é
 *   um contador em memória, e no caso comum vem um 304 sem corpo.
 * - `sync_after_seconds` — quanto esperar antes de **sincronizar**, depois de
 *   ver que a versão mudou. Caro: o sync segura uma conexão do pool de banco
 *   pela requisição inteira, e o servidor satura em ~440 deles por segundo.
 *
 * Ambos já vêm com jitter calculado no servidor. O player não sorteia nada — e
 * é de propósito: assim dá para alargar as janelas em produção sem publicar uma
 * versão nova do player.
 */

/** Intervalo usado enquanto o servidor não disser outro. */
const DEFAULT_POLL_SECONDS = 5;

/** Teto de espera após falhas seguidas, para não martelar um servidor caído. */
const MAX_BACKOFF_SECONDS = 60;

export interface ContentPollingConfig {
  /** Base da API, ex.: `https://api.efflux.media` */
  baseUrl: string;
  /** Identificador da tela. Não vai na consulta — o endpoint é o mesmo para
   *  todas —, mas o SyncService o atualiza em runtime e a paridade de interface
   *  (IContentDiscoveryService) evita um caso especial lá. */
  deviceId?: string;
  /** Tenant da tela. Chave de cache no servidor — sem ele, a tela também é
   *  avisada por mudanças de outros tenants (custa um sync a mais, não erra). */
  tenantId?: string;
  /** Só para teste; em produção o intervalo vem do servidor. */
  pollSeconds?: number;
  fetchImpl?: typeof fetch;
}

export class ContentPollingService implements IContentDiscoveryService {
  private readonly config: Required<Pick<ContentPollingConfig, 'baseUrl'>> &
    ContentPollingConfig;
  private readonly doFetch: typeof fetch;

  private state: SSEConnectionState = 'disconnected';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private etag: string | null = null;
  private lastVersion: number | null = null;
  private lastOkAt: number | null = null;
  private consecutiveFailures = 0;
  private running = false;

  private readonly eventCallbacks = new Set<SSEEventCallback>();
  private readonly stateCallbacks = new Set<SSEStateCallback>();
  private readonly heartbeatCallbacks = new Set<(timestamp: number) => void>();

  constructor(config: ContentPollingConfig) {
    this.config = config;
    this.doFetch = config.fetchImpl ?? fetch;
  }

  connect(): void {
    if (this.running) return;
    this.running = true;
    this.setState('connecting');
    void this.tick();
  }

  disconnect(): void {
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.setState('disconnected');
  }

  isConnected(): boolean {
    return this.state === 'connected';
  }

  getState(): SSEConnectionState {
    return this.state;
  }

  /**
   * Cada resposta bem-sucedida conta como heartbeat.
   *
   * No SSE o heartbeat existe porque a conexão pode morrer em silêncio e só o
   * silêncio denuncia. Aqui o equivalente natural é a última consulta que deu
   * certo: se as consultas param de responder, o servidor está inalcançável — o
   * que é exatamente o que o monitor de conectividade do player quer saber.
   */
  getLastHeartbeatTime(): number | null {
    return this.lastOkAt;
  }

  onContentChange(callback: SSEEventCallback): () => void {
    this.eventCallbacks.add(callback);
    return () => this.eventCallbacks.delete(callback);
  }

  onStateChange(callback: SSEStateCallback): () => void {
    this.stateCallbacks.add(callback);
    return () => this.stateCallbacks.delete(callback);
  }

  onHeartbeat(callback: (timestamp: number) => void): () => void {
    this.heartbeatCallbacks.add(callback);
    return () => this.heartbeatCallbacks.delete(callback);
  }

  /**
   * Zera o backoff e volta a consultar no intervalo normal.
   *
   * No antigo stream SSE (2ª conexão, já removida) este método destravava o
   * circuit breaker, que abria depois de N reconexões falhas. A consulta não
   * tem circuit breaker e nunca
   * desiste: o backoff exponencial tem teto de 60 s, então mesmo com o servidor
   * fora do ar ela continua tentando uma vez por minuto — barato o bastante
   * para não precisar de disjuntor, e a tela volta sozinha quando o servidor
   * voltar.
   *
   * O que o reset faz aqui é encurtar essa espera: depois de um refresh de
   * token ou de a rede voltar, não faz sentido aguardar o backoff inteiro.
   */
  reset(): void {
    this.consecutiveFailures = 0;
    if (!this.running) {
      this.connect();
      return;
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    void this.tick();
  }

  /**
   * Garante que as consultas estão de pé.
   *
   * O SyncService o chama (via `IContentDiscoveryService`); no antigo stream SSE
   * (removido) ele ressuscitava uma conexão zumbi — `EventSource` pode ficar em
   * `CLOSED` sem avisar ninguém. Aqui não há conexão para apodrecer: ou o
   * `setTimeout` está agendado, ou não está. Se não estiver, religa.
   */
  ensureConnected(): void {
    if (!this.running) {
      this.connect();
      return;
    }
    if (!this.timer) {
      void this.tick();
    }
  }

  /**
   * Troca a tela sem perder a linha de base.
   *
   * A versão conhecida é preservada de propósito: ela descreve o CONTEÚDO do
   * tenant, não a tela. Zerá-la faria a próxima consulta parecer uma mudança e
   * disparar um sync desnecessário.
   */
  updateDeviceId(deviceId: string): void {
    this.config.deviceId = deviceId;
  }

  private setState(next: SSEConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.stateCallbacks.forEach((cb) => cb(next));
  }

  private url(): string {
    const base = `${this.config.baseUrl}/api/v1/content-version`;
    return this.config.tenantId
      ? `${base}?tenant=${encodeURIComponent(this.config.tenantId)}`
      : base;
  }

  private async tick(): Promise<void> {
    if (!this.running) return;

    let nextInSeconds = this.config.pollSeconds ?? DEFAULT_POLL_SECONDS;

    try {
      const headers: Record<string, string> = {};
      if (this.etag) headers['If-None-Match'] = this.etag;

      const sentAt = Date.now();
      const resp = await this.doFetch(this.url(), { headers });
      // Relógio de graça: a consulta já acontece a cada poucos segundos; se o servidor
      // carimbar a hora no header (vem inclusive no 304), vira amostra NTP-like com RTT
      // medido — muito mais precisa que a batida do SSE, e disponível em modo polling.
      recordSyncSample(
        resp.headers?.get?.('X-Server-Time'),
        sentAt,
        Date.now(),
      );

      // O Retry-After vem inclusive no 304 — que é o caso comum. Sem lê-lo ali,
      // o player ficaria preso no intervalo default justamente na resposta que
      // mais recebe, e o servidor perderia o controle da carga.
      const retryAfter = resp.headers?.get?.('Retry-After');
      if (retryAfter) {
        const parsed = Number(retryAfter);
        if (Number.isFinite(parsed) && parsed > 0) nextInSeconds = parsed;
      }

      const tag = resp.headers?.get?.('ETag');
      if (tag) this.etag = tag;

      if (resp.status === 304) {
        this.onSuccess();
      } else if (resp.ok) {
        const body = (await resp.json()) as {
          version?: number;
          poll_after_seconds?: number;
          sync_after_seconds?: number;
        };
        this.onSuccess();

        if (
          typeof body.poll_after_seconds === 'number' &&
          body.poll_after_seconds > 0
        ) {
          nextInSeconds = body.poll_after_seconds;
        }

        const mudou =
          this.lastVersion !== null && body.version !== this.lastVersion;
        if (typeof body.version === 'number') this.lastVersion = body.version;

        // A primeira resposta apenas estabelece a linha de base. Emitir
        // mudança nela faria toda tela que liga sincronizar de imediato — e
        // numa queda de energia, a loja inteira liga junta.
        if (mudou) {
          this.emitContentChanged(body.sync_after_seconds);
        }
      } else {
        // 🔴 4xx/5xx trazem corpo de verdade (JSON/HTML de erro) e nada aqui o
        // lê — largar a `Response` assim segura o data pipe do Chromium e
        // reserva ~2 MB de address space por requisição no renderer 32-bit.
        // Mesma causa-raiz do renderer-kill do Tab A7 (vide commit 683464d).
        // O 304 acima NÃO precisa disto: resposta sem corpo, provado por A/B no
        // aparelho (bloquear o content-version não mudou o crescimento).
        await resp.body?.cancel().catch(() => undefined);
        this.onFailure();
        nextInSeconds = this.backoffSeconds();
      }
    } catch {
      this.onFailure();
      nextInSeconds = this.backoffSeconds();
    }

    if (!this.running) return;
    this.timer = setTimeout(() => void this.tick(), nextInSeconds * 1000);
  }

  private onSuccess(): void {
    this.consecutiveFailures = 0;
    this.lastOkAt = Date.now();
    this.heartbeatCallbacks.forEach((cb) => cb(this.lastOkAt as number));
    this.setState('connected');
  }

  private onFailure(): void {
    this.consecutiveFailures += 1;
    this.setState('reconnecting');
  }

  private backoffSeconds(): number {
    const base = this.config.pollSeconds ?? DEFAULT_POLL_SECONDS;
    return Math.min(
      base * 2 ** Math.min(this.consecutiveFailures, 6),
      MAX_BACKOFF_SECONDS,
    );
  }

  /**
   * Anuncia a mudança carregando o atraso de sync que o servidor mandou.
   *
   * O consumidor precisa respeitá-lo: quando a versão muda, as 20.000 telas
   * descobrem dentro de uma janela de segundos, e sincronizar na hora derruba
   * o pool de banco. É o thundering herd medido em 2026-08-06, que custava
   * 0,77 conexão de fila por tela.
   */
  private emitContentChanged(syncAfterSeconds?: number): void {
    const event: SSEEvent = {
      type: 'content_changed',
      data: {
        event_type: 'content_changed',
        reason: 'polling',
        timestamp: Date.now(),
        ...(typeof syncAfterSeconds === 'number' ? { syncAfterSeconds } : {}),
      },
      timestamp: Date.now(),
    };
    this.eventCallbacks.forEach((cb) => cb(event));
  }
}
